/**
 * ============================================================================
 * USER / TENANT PAGE: General Messages (Direct WhatsApp Messenger)
 * ============================================================================
 * Purpose:
 *   Single-page direct WhatsApp messaging interface without multi-step wizard.
 *   - Left Side: Sender number select, template picker, header media (upload/URL),
 *                recipients (manual numbers or Excel import), dynamic parameter inputs.
 *   - Right Side: Interactive sticky WhatsApp live preview phone mockup.
 *   - Auto-Memory: Automatically saves and restores parameter mappings and inputs
 *                  per template in localStorage so repetitive messages take seconds.
 *   - Continuous Sending: Message sends directly with SweetAlert2 confirmation
 *                         without page refresh; user can adjust phone number and
 *                         dispatch another message immediately.
 * ============================================================================
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Swal from "sweetalert2";
import { campaignService } from "../../services";
import phoneNumberService from "../../services/phoneNumberService";
import WhatsAppPreview from "../templates/WhatsAppPreview";

// --- SVG Icons ---
const PaperAirplaneIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
  </svg>
);

const ArrowUpTrayIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
  </svg>
);

const LinkIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-4.043 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
  </svg>
);

const PhotoIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
  </svg>
);

const VideoCameraIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
  </svg>
);

const DocumentTextIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
  </svg>
);

const CheckCircleIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

const ArrowPathIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);

const SparklesIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
  </svg>
);

export default function GeneralMessages() {
  // --- Core States ---
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(false);
  const [senderNumber, setSenderNumber] = useState("");

  // Audience / Recipient input states
  const [audienceType, setAudienceType] = useState("manual"); // "manual" | "excel"
  const [manualNumbers, setManualNumbers] = useState("");
  const [excelData, setExcelData] = useState(null);
  const [phoneColumn, setPhoneColumn] = useState("");
  const [isUploadingExcel, setIsUploadingExcel] = useState(false);

  // Dynamic Parameter Mapping
  const [variableMapping, setVariableMapping] = useState({});
  const [hasSavedMemory, setHasSavedMemory] = useState(false);

  // Header Media Asset States
  const [mediaSource, setMediaSource] = useState("upload"); // "upload" | "url"
  const [mediaUrl, setMediaUrl] = useState("");
  const [uploadedMediaName, setUploadedMediaName] = useState("");
  const [uploadedMediaSize, setUploadedMediaSize] = useState("");
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);

  // Send status
  const [isSending, setIsSending] = useState(false);

  const fileInputRef = useRef(null);
  const excelInputRef = useRef(null);

  // --- 1. Load Initial Data (Approved Templates & Active Sender Numbers) ---
  const loadInitialData = useCallback(async () => {
    setLoadingTemplates(true);
    setLoadingPhoneNumbers(true);

    try {
      const [tplRes, phoneRes] = await Promise.all([
        campaignService.getUserTemplates(),
        phoneNumberService.get({ limit: 100 }).catch(() => ({ data: [] })),
      ]);

      const tList = tplRes?.data || tplRes?.templates || (Array.isArray(tplRes) ? tplRes : []);
      setTemplates(tList);

      const pList = phoneRes?.data || (Array.isArray(phoneRes) ? phoneRes : []);
      setPhoneNumbers(pList);

      // Restore last used sender number or default to first verified
      const lastSender = localStorage.getItem("digi_gen_last_sender");
      if (lastSender && pList.some((p) => p.str_phone_number === lastSender)) {
        setSenderNumber(lastSender);
      } else if (pList.length > 0) {
        setSenderNumber(pList[0].str_phone_number);
      }

      // Restore last used template or default to first APPROVED template
      const approvedOnly = tList.filter((t) => (t.status || t.str_status || "").toUpperCase() === "APPROVED");
      const lastTplName = localStorage.getItem("digi_gen_last_template");
      if (lastTplName && approvedOnly.some((t) => t.name === lastTplName)) {
        const found = approvedOnly.find((t) => t.name === lastTplName);
        handleSelectTemplate(found);
      } else if (approvedOnly.length > 0) {
        handleSelectTemplate(approvedOnly[0]);
      }
    } catch (err) {
      console.error("Failed to load initial data for General Messages:", err);
    } finally {
      setLoadingTemplates(false);
      setLoadingPhoneNumbers(false);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // --- 2. Select Template & Restore Memory from LocalStorage ---
  const handleSelectTemplate = (template) => {
    if (!template) return;
    const isApproved = (template.status || template.str_status || "").toUpperCase() === "APPROVED";
    if (!isApproved) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: "Template Not Approved",
        text: `"${template.name}" is pending approval from Meta and cannot be dispatched.`,
        timer: 3500,
        showConfirmButton: false,
      });
      return;
    }
    setSelectedTemplate(template);
    localStorage.setItem("digi_gen_last_template", template.name);

    // Reset media unless template has media header
    if (!["IMAGE", "VIDEO", "DOCUMENT"].includes(template.headerType)) {
      setMediaUrl("");
      setUploadedMediaName("");
      setUploadedMediaSize("");
    }

    // Try loading saved variable memory for this template
    const memoryKey = `digi_gen_vars_${template.name}`;
    let loadedFromMemory = false;
    let initialMapping = {};

    try {
      const saved = localStorage.getItem(memoryKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === "object") {
          initialMapping = parsed;
          loadedFromMemory = true;
        }
      }
    } catch (_) {}

    // Ensure all variables present in the template have a default key
    const currentVars = template.variables || [];
    currentVars.forEach((v) => {
      if (!initialMapping[v]) {
        initialMapping[v] = { type: "static", value: "" };
      }
    });

    setVariableMapping(initialMapping);
    setHasSavedMemory(loadedFromMemory);
  };

  // --- 3. Save Variable Changes to Memory in Real-time ---
  const handleVariableChange = (varKey, type, value) => {
    const updated = {
      ...variableMapping,
      [varKey]: { type, value },
    };
    setVariableMapping(updated);

    if (selectedTemplate) {
      try {
        localStorage.setItem(`digi_gen_vars_${selectedTemplate.name}`, JSON.stringify(updated));
        setHasSavedMemory(true);
      } catch (_) {}
    }
  };

  // Reset variable memory for current template
  const handleClearSavedMemory = () => {
    if (!selectedTemplate) return;
    localStorage.removeItem(`digi_gen_vars_${selectedTemplate.name}`);
    const emptyMapping = {};
    (selectedTemplate.variables || []).forEach((v) => {
      emptyMapping[v] = { type: "static", value: "" };
    });
    setVariableMapping(emptyMapping);
    setHasSavedMemory(false);
  };

  // --- 4. Sender Number Selection & Persistence ---
  const handleSenderChange = (num) => {
    setSenderNumber(num);
    localStorage.setItem("digi_gen_last_sender", num);
  };

  // --- 5. Media Upload Handler ---
  const handleMediaFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingMedia(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await campaignService.uploadMedia(formData);
      if (res && res.success) {
        setMediaUrl(res.url);
        setUploadedMediaName(res.fileName || file.name);
        setUploadedMediaSize(file.size ? `${(file.size / 1024).toFixed(1)} KB` : "");
      } else {
        throw new Error(res?.error || "Failed to upload file");
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Upload Error",
        text: err.response?.data?.error || err.message,
      });
    } finally {
      setIsUploadingMedia(false);
    }
  };

  // --- 6. Excel Upload Handler ---
  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingExcel(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await campaignService.uploadExcel(formData);
      if (res && res.success) {
        setExcelData(res);
        setPhoneColumn(res.detectedPhoneColumn || res.columns?.[0] || "");

        // Auto-match template variables to columns by name
        if (selectedTemplate?.variables) {
          const autoMapped = { ...variableMapping };
          selectedTemplate.variables.forEach((v) => {
            const clean = v.replace(/[{}]/g, "").toLowerCase().trim();
            const matchingCol = res.columns.find((c) => c.toLowerCase().trim() === clean);
            if (matchingCol) {
              autoMapped[v] = { type: "column", value: matchingCol };
            }
          });
          setVariableMapping(autoMapped);
        }
      } else {
        throw new Error(res?.error || "Failed to process Excel sheet");
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Excel Import Error",
        text: err.response?.data?.error || err.message,
      });
    } finally {
      setIsUploadingExcel(false);
    }
  };

  // --- 7. Computed Recipients Count ---
  const parsedManualNumbers = useMemo(() => {
    if (!manualNumbers.trim()) return [];
    return manualNumbers
      .split(/[\n,;]+/)
      .map((s) => s.trim().replace(/[^0-9]/g, ""))
      .filter((s) => s.length >= 10);
  }, [manualNumbers]);

  const recipientCount = useMemo(() => {
    if (audienceType === "excel") {
      return excelData?.totalRows || 0;
    }
    return parsedManualNumbers.length;
  }, [audienceType, excelData, parsedManualNumbers]);

  // --- 8. Compute Live Samples for WhatsApp Preview ---
  const previewSamples = useMemo(() => {
    if (!selectedTemplate?.variables) return {};
    const samples = {};
    const sampleRow = excelData?.sampleRows?.[0] || {};

    selectedTemplate.variables.forEach((v) => {
      const config = variableMapping[v];
      if (config) {
        if (config.type === "column" && config.value) {
          samples[v] = sampleRow[config.value] !== undefined ? String(sampleRow[config.value]) : `[${config.value}]`;
        } else if (config.value && String(config.value).trim() !== "") {
          samples[v] = config.value;
        } else {
          samples[v] = `[${v}]`;
        }
      } else {
        samples[v] = `[${v}]`;
      }
    });

    return samples;
  }, [selectedTemplate, variableMapping, excelData]);

  // Check if all variables have been filled
  const allVariablesFilled = useMemo(() => {
    if (!selectedTemplate?.variables || selectedTemplate.variables.length === 0) return true;
    return selectedTemplate.variables.every((v) => {
      const item = variableMapping[v];
      return item && item.value && String(item.value).trim() !== "";
    });
  }, [selectedTemplate, variableMapping]);

  // --- 9. Direct Send Action (No Page Reload) ---
  const handleSendMessage = async () => {
    if (!selectedTemplate) {
      return Swal.fire({
        icon: "warning",
        title: "Template Required",
        text: "Please select an approved WhatsApp template to send.",
        confirmButtonColor: "#2563eb",
      });
    }

    if (!senderNumber) {
      return Swal.fire({
        icon: "warning",
        title: "Sender Number Required",
        text: "Please select an authorized WhatsApp Business phone number.",
        confirmButtonColor: "#2563eb",
      });
    }

    if (recipientCount === 0) {
      return Swal.fire({
        icon: "warning",
        title: "No Recipients Provided",
        text: audienceType === "excel" ? "Please upload an Excel sheet containing recipient numbers." : "Please enter at least one valid recipient phone number with country code.",
        confirmButtonColor: "#2563eb",
      });
    }

    // Media verification if template requires media
    if (["IMAGE", "VIDEO", "DOCUMENT"].includes(selectedTemplate.headerType) && !mediaUrl) {
      return Swal.fire({
        icon: "warning",
        title: "Header Media Required",
        text: `Template "${selectedTemplate.name}" requires a ${selectedTemplate.headerType} asset. Please upload a file or enter a direct URL.`,
        confirmButtonColor: "#2563eb",
      });
    }

    if (!allVariablesFilled) {
      return Swal.fire({
        icon: "warning",
        title: "Unmapped Variables",
        text: "Please provide values or column mappings for all template variables (highlighted in red).",
        confirmButtonColor: "#2563eb",
      });
    }

    // Confirmation modal
    const confirmResult = await Swal.fire({
      title: "Dispatch General Message?",
      html: `
        <div style="text-align: left; font-size: 13px; line-height: 1.6;">
          <p>• <b>Template:</b> ${selectedTemplate.name} (${(selectedTemplate.category || "MARKETING").toUpperCase()})</p>
          <p>• <b>Sender:</b> ${senderNumber}</p>
          <p>• <b>Recipients:</b> ${recipientCount} destination(s)</p>
          <p style="color: #6b7280; margin-top: 6px;">This message will be dispatched immediately through the telecom gateway.</p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Send Now! 🚀",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#2563eb",
    });

    if (!confirmResult.isConfirmed) return;

    setIsSending(true);

    try {
      // Build recipients payload
      let recipients = [];
      if (audienceType === "excel" && excelData) {
        recipients = (excelData.rows || []).map((row) => {
          const rawPhone = row[phoneColumn];
          const vars = {};
          if (selectedTemplate.variables) {
            selectedTemplate.variables.forEach((v) => {
              const mapped = variableMapping[v];
              const cleanKey = v.replace(/[{}]/g, "");
              if (mapped?.type === "column" && mapped.value) {
                vars[cleanKey] = row[mapped.value] !== undefined ? String(row[mapped.value]) : "";
              } else if (mapped?.value) {
                vars[cleanKey] = mapped.value;
              }
            });
          }
          return { number: String(rawPhone || ""), variables: vars };
        });
      } else {
        const staticVars = {};
        if (selectedTemplate.variables) {
          selectedTemplate.variables.forEach((v) => {
            const cleanKey = v.replace(/[{}]/g, "");
            staticVars[cleanKey] = variableMapping[v]?.value || "";
          });
        }
        recipients = parsedManualNumbers.map((num) => ({
          number: num,
          variables: staticVars,
        }));
      }

      const selectedPhoneObj = phoneNumbers.find((p) => p.str_phone_number === senderNumber);

      const payload = {
        is_general_message: true,
        send_now: true,
        str_template_name: selectedTemplate.name,
        str_sender_number: senderNumber,
        str_phone_number_id: selectedPhoneObj?.str_phone_number_id || senderNumber,
        str_waba_id: selectedTemplate.waba_id || selectedPhoneObj?.str_waba_id || "",
        str_operator: selectedPhoneObj?.str_operator || "karix",
        str_campaign_type: selectedTemplate.category || "MARKETING",
        obj_template_data: {
          id: selectedTemplate.id,
          name: selectedTemplate.name,
          category: selectedTemplate.category,
          language: selectedTemplate.language || "en",
          headerType: selectedTemplate.headerType || "NONE",
          headerText: selectedTemplate.headerText || "",
          bodyText: selectedTemplate.bodyText || "",
          footerText: selectedTemplate.footerText || "",
          buttons: selectedTemplate.buttons || [],
          headerMedia: mediaUrl ? { url: mediaUrl, fileName: uploadedMediaName } : null,
        },
        obj_variable_mapping: variableMapping,
        recipients,
      };

      const res = await campaignService.createCampaign(payload);

      if (res && res.success) {
        Swal.fire({
          icon: "success",
          title: "Message Sent Successfully! 🚀",
          html: `
            <p>Your message has been initiated for delivery to <b>${recipients.length}</b> recipient(s).</p>
            <p style="font-size: 11px; color: #6b7280; margin-top: 8px;">
              You can now enter a new number to send another message without leaving this page.
            </p>
          `,
          confirmButtonColor: "#2563eb",
          confirmButtonText: "Done",
        });
        // We purposefully DO NOT reload or reset the form.
        // User can change the phone number and send again!
      } else {
        throw new Error(res?.error || "Could not dispatch general message");
      }
    } catch (err) {
      console.error("General message send error:", err);
      Swal.fire({
        icon: "error",
        title: "Dispatch Failed",
        text: err.response?.data?.error || err.message || "Failed to dispatch message.",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* ── TOP HEADER BANNER ── */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700 shadow-xs flex items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-600 text-white flex items-center justify-center shrink-0 shadow-md shadow-blue-500/20">
          <PaperAirplaneIcon className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-base font-bold text-gray-900 dark:text-white">
              General Messages
            </h1>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800">
              Direct Messenger
            </span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">
            Fast, unified WhatsApp messaging without campaign overhead. Pick a template, customize dynamic values, add recipients, and dispatch directly.
          </p>
        </div>
      </div>

      {/* ── 2-COLUMN MAIN INTERFACE ── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* ── LEFT COLUMN: UNIFIED FORM CONTROLS ── */}
        <div className="lg:col-span-7 xl:col-span-8 space-y-5">
          {/* 1. SENDER NUMBER & TEMPLATE SELECTION CARD */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700 shadow-xs space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-700/60">
              <h2 className="text-xs font-bold uppercase tracking-wider text-gray-800 dark:text-gray-200 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-600"></span>
                <span>1. Channel & Template Setup</span>
              </h2>
              {hasSavedMemory && (
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded-md border border-emerald-200 dark:border-emerald-800/40">
                    <SparklesIcon className="w-3 h-3" />
                    <span>Values Memory Restored</span>
                  </span>
                  <button
                    type="button"
                    onClick={handleClearSavedMemory}
                    className="text-[11px] text-gray-400 hover:text-red-500 hover:underline cursor-pointer"
                    title="Clear saved memory for this template"
                  >
                    Reset
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Sender Number Select */}
              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300">
                  Sender WhatsApp Number <span className="text-red-500">*</span>
                </label>
                {loadingPhoneNumbers ? (
                  <div className="p-2 text-xs text-gray-400">Loading sender numbers...</div>
                ) : (
                  <select
                    value={senderNumber}
                    onChange={(e) => handleSenderChange(e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white outline-hidden cursor-pointer"
                  >
                    {phoneNumbers.length === 0 && <option value="">No phone numbers found</option>}
                    {phoneNumbers.map((p) => (
                      <option key={p.str_phone_number} value={p.str_phone_number}>
                        {p.str_display_phone_number || p.str_phone_number} {p.str_verified_name ? `(${p.str_verified_name})` : ""}
                      </option>
                    ))}
                  </select>
                )}
                <span className="text-[10px] text-gray-400">
                  Select which active business number to broadcast from
                </span>
              </div>

              {/* Template Picker */}
              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300">
                  Select Approved Template <span className="text-red-500">*</span>
                </label>
                {loadingTemplates ? (
                  <div className="p-2 text-xs text-gray-400">Loading templates...</div>
                ) : (
                  <select
                    value={selectedTemplate?.name || ""}
                    onChange={(e) => {
                      const tpl = templates.find((t) => t.name === e.target.value);
                      handleSelectTemplate(tpl);
                    }}
                    className="w-full px-3 py-2 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white outline-hidden cursor-pointer font-medium"
                  >
                    {templates.map((t) => {
                      const isApproved = (t.status || t.str_status || "").toUpperCase() === "APPROVED" || t.is_approved === true;
                      return (
                        <option 
                          key={t.id || t.name} 
                          value={t.name}
                          disabled={!isApproved}
                          className={!isApproved ? "text-gray-400 bg-gray-100 dark:bg-gray-800" : ""}
                        >
                          {isApproved ? "✓ " : "⏳ [PENDING] "}{t.name} ({t.category} · {t.language}) {!isApproved ? "— Not Available" : ""}
                        </option>
                      );
                    })}
                  </select>
                )}
                {selectedTemplate && (
                  <div className="flex items-center gap-2 text-[10px] text-gray-400">
                    <span>Category: <b className="text-gray-700 dark:text-gray-300">{selectedTemplate.category}</b></span>
                    <span>·</span>
                    <span>Variables: <b className="text-blue-600 dark:text-blue-400">{selectedTemplate.variableCount || 0}</b></span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 2. HEADER MEDIA ASSET (Conditional: Only if template requires media) */}
          {selectedTemplate && ["IMAGE", "VIDEO", "DOCUMENT"].includes(selectedTemplate.headerType) && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700 shadow-xs space-y-3.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="p-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg">
                    {selectedTemplate.headerType === "DOCUMENT" && <DocumentTextIcon className="w-4 h-4" />}
                    {selectedTemplate.headerType === "IMAGE" && <PhotoIcon className="w-4 h-4" />}
                    {selectedTemplate.headerType === "VIDEO" && <VideoCameraIcon className="w-4 h-4" />}
                  </span>
                  <div>
                    <h2 className="text-xs font-bold uppercase tracking-wider text-gray-800 dark:text-gray-200">
                      2. Header Media Asset ({selectedTemplate.headerType})
                    </h2>
                    <span className="text-[10px] text-gray-400">
                      Upload from computer or enter a direct public URL
                    </span>
                  </div>
                </div>

                {/* Source Toggle */}
                <div className="flex items-center bg-gray-100 dark:bg-gray-700/60 p-0.5 rounded-lg text-[11px] font-semibold">
                  <button
                    type="button"
                    onClick={() => setMediaSource("upload")}
                    className={`px-2.5 py-1 rounded-md transition-all cursor-pointer ${
                      mediaSource === "upload"
                        ? "bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-xs"
                        : "text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                    }`}
                  >
                    Upload File
                  </button>
                  <button
                    type="button"
                    onClick={() => setMediaSource("url")}
                    className={`px-2.5 py-1 rounded-md transition-all cursor-pointer ${
                      mediaSource === "url"
                        ? "bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-xs"
                        : "text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                    }`}
                  >
                    Direct URL
                  </button>
                </div>
              </div>

              {mediaSource === "upload" ? (
                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleMediaFileUpload}
                    className="hidden"
                    accept={
                      selectedTemplate.headerType === "IMAGE"
                        ? "image/*"
                        : selectedTemplate.headerType === "VIDEO"
                        ? "video/*"
                        : ".pdf,.doc,.docx,.xls,.xlsx"
                    }
                  />
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400 rounded-xl p-4 text-center cursor-pointer transition-colors bg-gray-50/50 dark:bg-gray-900/50"
                  >
                    <ArrowUpTrayIcon className="w-5 h-5 mx-auto text-gray-400 mb-1" />
                    <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                      {isUploadingMedia
                        ? "Uploading media..."
                        : uploadedMediaName
                        ? `Selected: ${uploadedMediaName} (${uploadedMediaSize})`
                        : `Click to choose ${selectedTemplate.headerType.toLowerCase()} file`}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      JPG, PNG, MP4, or PDF up to 16MB
                    </p>
                  </div>
                </div>
              ) : (
                <div className="relative">
                  <LinkIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="url"
                    placeholder="https://example.com/assets/banner.jpg"
                    value={mediaUrl}
                    onChange={(e) => {
                      setMediaUrl(e.target.value);
                      setUploadedMediaName(e.target.value.split("/").pop() || "");
                    }}
                    className="w-full pl-9 pr-3 py-2 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-hidden font-mono"
                  />
                </div>
              )}
            </div>
          )}

          {/* 3. RECIPIENTS CARD (MANUAL OR EXCEL IMPORT) */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700 shadow-xs space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-700/60">
              <h2 className="text-xs font-bold uppercase tracking-wider text-gray-800 dark:text-gray-200 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-indigo-600"></span>
                <span>2. Target Recipients</span>
              </h2>

              {/* Mode Toggle */}
              <div className="flex items-center bg-gray-100 dark:bg-gray-700/60 p-0.5 rounded-lg text-[11px] font-semibold">
                <button
                  type="button"
                  onClick={() => setAudienceType("manual")}
                  className={`px-3 py-1 rounded-md transition-all cursor-pointer ${
                    audienceType === "manual"
                      ? "bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-xs"
                      : "text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                  }`}
                >
                  Manual Entry
                </button>
                <button
                  type="button"
                  onClick={() => setAudienceType("excel")}
                  className={`px-3 py-1 rounded-md transition-all cursor-pointer ${
                    audienceType === "excel"
                      ? "bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-xs"
                      : "text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                  }`}
                >
                  Excel Upload
                </button>
              </div>
            </div>

            {audienceType === "manual" ? (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <label className="font-semibold text-gray-700 dark:text-gray-300">
                    Recipient Phone Number(s) <span className="text-red-500">*</span>
                  </label>
                  <span className="text-[11px] font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-md">
                    {recipientCount} number{recipientCount !== 1 ? "s" : ""} detected
                  </span>
                </div>
                <textarea
                  rows={3}
                  value={manualNumbers}
                  onChange={(e) => setManualNumbers(e.target.value)}
                  placeholder="919876543210&#10;919876543211 (one number per line or comma-separated)"
                  className="w-full p-3 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl font-mono text-gray-900 dark:text-white outline-hidden focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-[10px] text-gray-400 block">
                  💡 Tip: Include country code without '+' (e.g. 91 for India). You can enter a single number or multiple.
                </span>
              </div>
            ) : (
              <div className="space-y-3">
                <input
                  type="file"
                  ref={excelInputRef}
                  onChange={handleExcelUpload}
                  className="hidden"
                  accept=".xlsx,.xls,.csv"
                />
                <div
                  onClick={() => excelInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-200 dark:border-gray-700 hover:border-blue-500 rounded-xl p-4 text-center cursor-pointer transition-colors bg-gray-50/50 dark:bg-gray-900/50"
                >
                  <ArrowUpTrayIcon className="w-5 h-5 mx-auto text-gray-400 mb-1" />
                  <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    {isUploadingExcel
                      ? "Reading Excel data..."
                      : excelData
                      ? `Selected: ${excelData.fileName} (${excelData.totalRows} rows)`
                      : "Click to upload .xlsx, .xls or .csv file"}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    Columns will be auto-detected for phone numbers & template variables
                  </p>
                </div>

                {excelData && (
                  <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 flex items-center justify-between text-xs">
                    <span className="text-gray-600 dark:text-gray-400 font-medium">
                      Select Phone Number Column:
                    </span>
                    <select
                      value={phoneColumn}
                      onChange={(e) => setPhoneColumn(e.target.value)}
                      className="px-2.5 py-1 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg font-mono cursor-pointer outline-hidden"
                    >
                      {excelData.columns.map((c) => (
                        <option key={c} value={c}>
                          Column: {c}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 4. DYNAMIC PARAMETER MAPPING (2 COLUMNS PER ROW, 3 ROWS VISIBLE) */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700 shadow-xs space-y-3.5">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-700/60">
              <div>
                <h2 className="text-xs font-bold uppercase tracking-wider text-gray-800 dark:text-gray-200 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  <span>3. Dynamic Parameters</span>
                </h2>
                <span className="text-[10px] text-gray-400">
                  {selectedTemplate?.variableCount ? `${selectedTemplate.variableCount} parameter(s) required` : "No parameters required"} · Auto-saved for future sends
                </span>
              </div>

              {selectedTemplate?.variables && selectedTemplate.variables.length > 6 && (
                <span className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-full">
                  Scroll for more
                </span>
              )}
            </div>

            {(!selectedTemplate?.variables || selectedTemplate.variables.length === 0) ? (
              <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 text-xs text-gray-500">
                ✨ This template has no dynamic body variables. All recipients will receive the standard text.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[292px] overflow-y-auto pr-1">
                {selectedTemplate.variables.map((v) => {
                  const currentVal = variableMapping[v]?.value || "";
                  const isFilled = String(currentVal).trim() !== "";

                  return (
                    <div
                      key={v}
                      className={`p-3 bg-gray-50 dark:bg-gray-900 rounded-xl border flex flex-col justify-between shrink-0 transition-all ${
                        isFilled
                          ? "border-emerald-300 dark:border-emerald-700/80"
                          : "border-red-300 dark:border-red-700/80"
                      }`}
                      style={{ height: "88px", minHeight: "88px" }}
                    >
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-mono font-bold text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/40 px-2 py-0.5 rounded-md text-[11px]">
                          Variable {v}
                        </span>
                        <span className="text-[10px] text-gray-400">
                          {audienceType === "excel" ? "Map Column" : "Value"}
                        </span>
                      </div>

                      {audienceType === "excel" && excelData ? (
                        <select
                          value={currentVal}
                          onChange={(e) => handleVariableChange(v, "column", e.target.value)}
                          className="w-full px-2.5 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg font-mono cursor-pointer outline-hidden focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">-- Choose Excel Column --</option>
                          {excelData.columns.map((c) => (
                            <option key={c} value={c}>
                              Column: {c}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          placeholder={`Enter value for ${v}...`}
                          value={currentVal}
                          onChange={(e) => handleVariableChange(v, "static", e.target.value)}
                          className="w-full px-2.5 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg outline-hidden focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 5. DIRECT DISPATCH ACTION BAR */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700 shadow-xs flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                  Ready to Dispatch:
                </span>
                <span className="text-xs font-bold text-blue-600 dark:text-blue-400">
                  {recipientCount} recipient{recipientCount !== 1 ? "s" : ""}
                </span>
                <span className="text-gray-400">·</span>
                <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                  From: {senderNumber || "None selected"}
                </span>
              </div>
              <p className="text-[11px] text-gray-400 mt-0.5">
                No campaign title needed. Dispatches directly and keeps page open for subsequent messages.
              </p>
            </div>

            <button
              type="button"
              onClick={handleSendMessage}
              disabled={isSending || recipientCount === 0 || !selectedTemplate || !senderNumber}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl text-xs shadow-md shadow-blue-500/25 transition-all cursor-pointer"
            >
              {isSending ? (
                <>
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  <span>Dispatching Message...</span>
                </>
              ) : (
                <>
                  <PaperAirplaneIcon className="w-4 h-4" />
                  <span>Send Message Now 🚀</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── RIGHT COLUMN: STICKY WHATSAPP LIVE PREVIEW ── */}
        <div className="lg:col-span-5 xl:col-span-4">
          <div className="sticky top-6 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center gap-2">
                <span>📱 WhatsApp Live Preview</span>
              </h3>
              {selectedTemplate && (
                <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/50 px-2 py-0.5 rounded-full border border-emerald-200 dark:border-emerald-800/60">
                  {selectedTemplate.category}
                </span>
              )}
            </div>

            <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs flex flex-col items-center justify-center">
              <WhatsAppPreview
                templateName={selectedTemplate?.name || "Template Preview"}
                headerType={selectedTemplate?.headerType || "NONE"}
                headerText={selectedTemplate?.headerText || ""}
                headerPreview={mediaUrl || null}
                mediaFileName={uploadedMediaName || (mediaUrl ? mediaUrl.split("/").pop().split("?")[0] : "")}
                mediaFileSize={uploadedMediaSize}
                bodyText={
                  selectedTemplate?.bodyText ||
                  "Please select an approved WhatsApp template on the left to preview how it will appear to your recipient."
                }
                footerText={selectedTemplate?.footerText || ""}
                buttons={selectedTemplate?.buttons || []}
                samples={previewSamples}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
